import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, Search, Building2 } from 'lucide-react';
import { brandsService } from '../../services/brandsService';
import { useAuthStore } from '../../store/authStore';
import { getBrandLogoUrl } from '../../utils/helpers';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';

export const BrandsListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [search, setSearch] = useState('');

  // Předschvalovatel smí značku jen upravit (logo, barva) - zakládat, mazat
  // ani přejmenovat nesmí. Stejné pravidlo hlídá i backend.
  const smiZakladatAMazat = user?.role === 'admin' || user?.role === 'approver';

  const { data: brands = [], isLoading } = useQuery({
    queryKey: ['brands'],
    queryFn: () => brandsService.getAllBrands(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => brandsService.deleteBrand(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brands'] });
    },
    // Bez tohohle selhavalo mazani potichu - znacku s produkty smazat nejde.
    onError: (e: any) => {
      alert(e?.response?.data?.message || 'Značku se nepodařilo smazat.');
    },
  });

  const handleDelete = async (id: string, name: string) => {
    if (window.confirm(`Opravdu chcete smazat značku "${name}"?`)) {
      // Chybu hlasi onError; tady jen at neskonci jako neodchycene odmitnuti.
      try {
        await deleteMutation.mutateAsync(id);
      } catch {
        /* ošetřeno v onError */
      }
    }
  };

  const filteredBrands = brands.filter((b) =>
    b.name.toLowerCase().includes(search.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Značky</h1>
          <p className="mt-2 text-gray-600">Spravujte značky produktů</p>
        </div>
        {smiZakladatAMazat && (
          <Button onClick={() => navigate('/brands/new')}>
            <Plus className="w-4 h-4 mr-2" />
            Nová značka
          </Button>
        )}
      </div>

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            type="text"
            placeholder="Hledat podle názvu..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {filteredBrands.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="text-gray-400 mb-4">
            <Building2 className="w-16 h-16 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Žádné značky nenalezeny</h3>
          <p className="text-gray-600 mb-6">
            {search ? 'Zkuste upravit vyhledávání' : 'Začněte vytvořením první značky'}
          </p>
          {!search && (
            <Button onClick={() => navigate('/brands/new')}>
              <Plus className="w-4 h-4 mr-2" />
              Vytvořit značku
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Značka
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Logo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Počet produktů
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Akce
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredBrands.map((brand) => (
                <tr key={brand.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 max-w-xs truncate" title={brand.name}>{brand.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {brand.id ? (
                      <img
                        src={getBrandLogoUrl(brand.id, brand.updatedAt)}
                        alt={brand.name}
                        className="w-12 h-12 object-contain"
                        onError={(e) => {
                          // If logo doesn't exist, show placeholder
                          (e.target as HTMLImageElement).style.display = 'none';
                          (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <div className="hidden w-12 h-12 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-400">
                      Bez loga
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {brand._count?.products ?? 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => navigate(`/brands/${brand.id}/edit`)}
                        title="Upravit značku"
                        className="text-blue-600 hover:text-blue-900 p-2 rounded hover:bg-blue-50"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      {smiZakladatAMazat && (
                        <button
                          onClick={() => handleDelete(brand.id, brand.name)}
                          title="Smazat značku – nejde u značky, na kterou se váže produkt, uživatel, promo nebo ikona"
                          className="text-red-600 hover:text-red-900 p-2 rounded hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
